import { useEffect, useRef, useState } from "react";
import { AppLayout } from "../components/Layout/AppLayout";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, Send, Plus, MessageSquare, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGERENCIAS = [
  "¿Cuáles son los sectores más críticos ahora mismo?",
  "Hazme un resumen de la actividad de esta semana",
  "¿Qué tipo de incidencia es más frecuente en Zona Nord?",
  "Compárame Nou Barris y Sant Andreu",
];

export default function Asistente() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamingText]);

  const loadSessions = () => api.get("/ai/sessions").then((r) => setSessions(r.data)).catch(() => {});

  const abrirSesion = async (sid) => {
    setSessionId(sid);
    setStreamingText("");
    const { data } = await api.get(`/ai/sessions/${sid}/messages`);
    setMessages(data);
  };

  const nuevaConversacion = () => {
    setSessionId(null);
    setMessages([]);
    setStreamingText("");
  };

  const enviar = async (textoForzado) => {
    const texto = (textoForzado ?? input).trim();
    if (!texto || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: texto }]);
    setStreaming(true);
    setStreamingText("");

    try {
      const res = await fetch(`${API_BASE}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: texto, session_id: sessionId }),
      });
      if (!res.ok || !res.body) throw new Error("No se pudo conectar con el asistente");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acumulado = "";
      let nuevaSessionId = sessionId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const partes = buffer.split("\n\n");
        buffer = partes.pop();
        for (const parte of partes) {
          if (!parte.startsWith("data: ")) continue;
          const data = JSON.parse(parte.slice(6));
          if (data.session_id) nuevaSessionId = data.session_id;
          if (data.delta) {
            acumulado += data.delta;
            setStreamingText(acumulado);
          }
          if (data.error) throw new Error(data.error);
        }
      }
      setSessionId(nuevaSessionId);
      setMessages((prev) => [...prev, { role: "assistant", content: acumulado }]);
      setStreamingText("");
      loadSessions();
    } catch (e) {
      toast.error(e.message || "Error al consultar al asistente");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <AppLayout title="Asistente IA">
      <div className="flex gap-4 h-[calc(100vh-140px)]" data-testid="ai-assistant-page">
        <div className="w-64 shrink-0 flex flex-col gap-2 border-r border-border pr-4">
          <Button onClick={nuevaConversacion} variant="outline" className="gap-2 justify-start" data-testid="ai-new-session-button">
            <Plus className="h-4 w-4" /> Nueva conversación
          </Button>
          <div className="flex-1 overflow-y-auto space-y-1 mt-2" data-testid="ai-sessions-list">
            {sessions.map((s) => (
              <button
                key={s.session_id}
                onClick={() => abrirSesion(s.session_id)}
                data-testid={`ai-session-item-${s.session_id}`}
                className={`w-full text-left px-3 py-2 rounded-md text-xs flex items-center gap-2 transition-colors ${
                  sessionId === s.session_id ? "bg-valoriza-leaf/15 text-valoriza-leaf font-bold" : "hover:bg-muted text-muted-foreground"
                }`}
              >
                <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{s.titulo}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 flex flex-col">
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pr-2" data-testid="ai-messages-container">
            {messages.length === 0 && !streamingText && (
              <div className="h-full flex flex-col items-center justify-center text-center gap-4 text-muted-foreground">
                <Sparkles className="h-10 w-10 text-valoriza-leaf" />
                <div>
                  <p className="font-bold text-foreground">Asistente IA de SENTINEL BCN</p>
                  <p className="text-sm">Pregúntame sobre fichas IRIS, incidencias, sectores o pídeme un informe.</p>
                </div>
                <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                  {SUGERENCIAS.map((s) => (
                    <button
                      key={s}
                      onClick={() => enviar(s)}
                      data-testid="ai-suggestion-chip"
                      className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-valoriza-leaf hover:text-valoriza-leaf transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-message-${m.role}-${i}`}>
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                    m.role === "user" ? "bg-valoriza-leaf text-white" : "bg-muted text-foreground"
                  }`}
                >
                  {m.role === "assistant" ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none prose-table:text-xs">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap">{m.content}</p>
                  )}
                </div>
              </div>
            ))}
            {streaming && (
              <div className="flex justify-start" data-testid="ai-streaming-message">
                <div className="max-w-[75%] rounded-2xl px-4 py-2.5 text-sm bg-muted text-foreground">
                  {streamingText ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none prose-table:text-xs">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingText}</ReactMarkdown>
                    </div>
                  ) : (
                    <Loader2 className="h-4 w-4 animate-spin text-valoriza-leaf" />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-end gap-2 pt-4 border-t border-border mt-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  enviar();
                }
              }}
              placeholder="Escribe tu pregunta... (Enter para enviar, Shift+Enter para salto de línea)"
              className="resize-none min-h-[48px] max-h-32"
              data-testid="ai-chat-input"
            />
            <Button onClick={() => enviar()} disabled={streaming || !input.trim()} size="icon" data-testid="ai-chat-send-button">
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
